import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import {
  Loader2, FileDown, Sparkles, MapPin, Gauge, Wallet, CalendarClock,
  CheckCircle2, Flag, Clock, MessageSquare, BookOpen, Compass,
} from "lucide-react";
import { toast } from "sonner";
import AdvisorChat from "@/components/AdvisorChat";

function StatCard({ value, label, icon: Icon, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-400">
        {Icon && <Icon className="h-3.5 w-3.5" />} {label}
      </div>
      <div className="mt-2 font-heading text-3xl font-semibold tracking-tight">{value}</div>
    </div>
  );
}

function PreparingKit() {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 py-16 text-center" data-testid="kit-preparing">
      <Loader2 className="h-7 w-7 animate-spin text-slate-400" />
      <h3 className="mt-5 font-heading text-xl font-semibold tracking-tight">Your full Success Kit is being prepared</h3>
      <p className="mt-2 max-w-md text-sm text-slate-500">Our AI consultant is writing your complete 25–35 page relocation strategy. This usually takes a couple of minutes — your roadmap is ready now in the Overview tab.</p>
    </div>
  );
}

function Roadmap({ roadmap, onDownload, downloading, status }) {
  const bs = roadmap.budget_snapshot || {};
  const budgetCards = [["Monthly", bs.monthly_total], ["Rent", bs.rent], ["Setup", bs.setup_cost], ["Emergency", bs.emergency_fund]]
    .filter(([, v]) => v != null);
  return (
    <div className="space-y-8" data-testid="roadmap-view">
      <div className="relative overflow-hidden rounded-2xl border border-slate-200">
        <div className="relative h-56">
          {roadmap.hero_image ? (
            <img src={roadmap.hero_image} alt={roadmap.destination_city} className="h-full w-full object-cover" />
          ) : (
            <div className="h-full w-full bg-primary" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/10" />
          <div className="absolute left-6 top-5 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-white/85">
            <Sparkles className="h-3.5 w-3.5" /> Personal Relocation Roadmap
          </div>
          <div className="absolute bottom-5 left-6 right-6">
            <div className="flex items-end justify-between gap-4">
              <div>
                <div className="font-heading text-3xl font-semibold tracking-tight text-white" data-testid="roadmap-city">{roadmap.destination_city}</div>
                <div className="flex items-center gap-1.5 text-sm text-white/80"><MapPin className="h-3.5 w-3.5" /> {roadmap.destination_country}</div>
              </div>
              <Button onClick={onDownload} disabled={downloading} variant="secondary" className="gap-2" data-testid="download-roadmap-btn">
                {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} Download
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard value={`${roadmap.overall_match_score}%`} label="Overall match" icon={Gauge} testid="roadmap-match" />
        <StatCard value={`${roadmap.ai_confidence_score}%`} label="AI confidence" icon={Sparkles} testid="roadmap-confidence" />
        <StatCard value={roadmap.estimated_timeline || "—"} label="Est. timeline" icon={CalendarClock} testid="roadmap-timeline" />
      </div>

      {roadmap.top_reasons?.length > 0 && (
        <div>
          <h3 className="font-heading text-lg font-semibold tracking-tight">Why this is your top match</h3>
          <ul className="mt-3 grid gap-2.5 sm:grid-cols-2">
            {roadmap.top_reasons.map((r, i) => (
              <li key={r} className="flex items-start gap-2.5 rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-700">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> {r}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(bs.monthly_total || bs.rent) && (
        <div>
          <h3 className="font-heading text-lg font-semibold tracking-tight">Budget snapshot</h3>
          <div className="mt-3 grid gap-4 sm:grid-cols-4">
            {budgetCards.map(([lbl, v]) => (
                <StatCard key={lbl} value={`$${Number(v).toLocaleString()}`} label={lbl} icon={Wallet} testid={`budget-${lbl.toLowerCase()}`} />
              ))}
          </div>
        </div>
      )}

      <div className="grid gap-8 lg:grid-cols-2">
        {roadmap.first_30_day_priorities?.length > 0 && (
          <div>
            <h3 className="flex items-center gap-2 font-heading text-lg font-semibold tracking-tight"><Flag className="h-4 w-4 text-slate-500" /> First 30-day priorities</h3>
            <ul className="mt-3 space-y-2">
              {roadmap.first_30_day_priorities.map((p, i) => (
                <li key={p} className="flex items-start gap-2.5 text-sm text-slate-700"><span className="mt-0.5 font-semibold text-slate-400">{i + 1}.</span> {p}</li>
              ))}
            </ul>
          </div>
        )}
        {roadmap.milestones?.length > 0 && (
          <div>
            <h3 className="flex items-center gap-2 font-heading text-lg font-semibold tracking-tight"><Clock className="h-4 w-4 text-slate-500" /> Relocation milestones</h3>
            <div className="mt-3 space-y-3">
              {roadmap.milestones.map((m, i) => (
                <div key={`${m.when}-${m.title}`} className="flex items-center gap-3">
                  <div className="w-20 shrink-0 text-xs font-semibold text-slate-500">{m.when}</div>
                  <div className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">{m.title}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {status !== "ready" && (
        <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600" data-testid="kit-building-banner">
          <Loader2 className="h-4 w-4 animate-spin" /> Your full 25–35 page Success Kit is being prepared in the background. The roadmap above is your quick reference in the meantime.
        </div>
      )}
    </div>
  );
}

export default function SuccessKit() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [kit, setKit] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingKit, setDownloadingKit] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (user && user.plan !== "success_kit") {
      navigate("/pricing");
      return;
    }
    let active = true;
    const load = async () => {
      try {
        const { data } = await api.get("/success-kit");
        if (!active) return;
        setKit(data);
        if (["generating", "building_kit"].includes(data.status)) {
          timer.current = setTimeout(load, 4000);
        }
      } catch {
        if (active) toast.error("Could not load your Success Kit");
      }
    };
    load();
    return () => { active = false; if (timer.current) clearTimeout(timer.current); };
  }, [user, navigate]);

  const downloadRoadmap = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/success-kit/roadmap.pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "canhelpmove-relocation-roadmap.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Roadmap is still being prepared");
    } finally {
      setDownloading(false);
    }
  };

  const downloadKit = async () => {
    setDownloadingKit(true);
    try {
      const res = await api.get("/success-kit/full.pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "canhelpmove-success-kit.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Your full Success Kit is still being prepared");
    } finally {
      setDownloadingKit(false);
    }
  };

  if (!kit) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;

  if (kit.status === "awaiting_assessment") {
    return (
      <div className="mx-auto max-w-lg px-6 py-24 text-center">
        <Compass className="mx-auto h-8 w-8 text-slate-400" />
        <h1 className="mt-4 font-heading text-2xl font-semibold">Let's build your Success Kit</h1>
        <p className="mt-2 text-slate-500">Complete a relocation assessment first — then we'll generate your personalized roadmap and full Success Kit.</p>
        <Button className="mt-6" onClick={() => navigate("/assessment")} data-testid="kit-start-assessment">Start assessment</Button>
      </div>
    );
  }

  const roadmapReady = kit.roadmap && kit.roadmap.destination_city;
  const sections = kit.sections || {};
  const order = kit.section_order || [];

  return (
    <div className="mx-auto max-w-5xl px-6 py-10 animate-fade-up" data-testid="success-kit-page">
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <Badge className="gap-1.5 bg-primary"><Sparkles className="h-3 w-3" /> Relocation Success Kit</Badge>
        {kit.ai_generated && <Badge variant="secondary" className="gap-1.5"><Sparkles className="h-3 w-3" /> AI-generated</Badge>}
      </div>
      <h1 className="font-heading text-3xl font-semibold tracking-tight">Your relocation hub</h1>
      <p className="mt-2 text-slate-500">Your roadmap, complete strategy, and AI advisor — everything for your move in one place.</p>

      <Tabs defaultValue="overview" className="mt-8">
        <TabsList data-testid="kit-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="kit" data-testid="tab-kit">Full Success Kit</TabsTrigger>
          <TabsTrigger value="advisor" data-testid="tab-advisor">AI Advisor</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          {roadmapReady ? (
            <Roadmap roadmap={kit.roadmap} onDownload={downloadRoadmap} downloading={downloading} status={kit.status} />
          ) : (
            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 py-16 text-center" data-testid="roadmap-preparing">
              <Loader2 className="h-7 w-7 animate-spin text-slate-400" />
              <h3 className="mt-5 font-heading text-xl font-semibold tracking-tight">Preparing your roadmap…</h3>
              <p className="mt-2 text-sm text-slate-500">This takes just a few seconds.</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="kit" className="mt-6">
          {kit.status === "ready" && Object.keys(sections).length > 0 ? (
            <>
              <div className="mb-5 flex flex-col items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="kit-pdf-banner">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary"><BookOpen className="h-5 w-5 text-white" /></div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Your complete Success Kit</div>
                    <div className="text-xs text-slate-500">Download the full {Object.keys(sections).length}-section relocation plan as a premium PDF.</div>
                  </div>
                </div>
                <Button onClick={downloadKit} disabled={downloadingKit} className="gap-2" data-testid="download-kit-pdf-btn">
                  {downloadingKit ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} Download Kit PDF
                </Button>
              </div>
              <Accordion type="single" collapsible className="space-y-2" data-testid="kit-sections">
              {order.map(({ key, title }) => {
                const s = sections[key];
                if (!s) return null;
                return (
                  <AccordionItem key={key} value={key} className="rounded-lg border border-slate-200 bg-white px-4">
                    <AccordionTrigger className="text-left font-heading text-base font-semibold" data-testid={`kit-section-${key}`}>{title}</AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-slate-700">
                      {s.summary && <p className="mb-2 font-medium text-slate-900">{s.summary}</p>}
                      {s.body && <p className="whitespace-pre-line">{s.body}</p>}
                      {s.bullets?.length > 0 && (
                        <ul className="mt-3 space-y-1.5">
                          {s.bullets.map((b, i) => (
                            <li key={b} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" /> {b}</li>
                          ))}
                        </ul>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
            </>
          ) : (
            <PreparingKit />
          )}
        </TabsContent>

        <TabsContent value="advisor" className="mt-6">
          <AdvisorChat />
        </TabsContent>
      </Tabs>
    </div>
  );
}
